import { FormEvent } from 'react'
import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Field } from '@/components/ui/Field'
import { InputField, TextareaField } from '@/components/shared/form'
import {
  Dialog,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/ui/Dialog'

export interface Role {
  id: string
  name: string
  key: string
  description: string | null
  permissions: string[]
}

interface RoleFormData {
  name: string
  key: string
  description: string
  permissions: string[]
}

interface RoleFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  role?: Role | null
  availablePermissions: string[]
}



export default function RoleFormDialog({
  open,
  onOpenChange,
  role,
  availablePermissions,
}: RoleFormDialogProps) {
  const isEdit = Boolean(role)

  const form = useForm<RoleFormData>({
    name: role?.name ?? '',
    key: role?.key ?? '',
    description: role?.description ?? '',
    permissions: role?.permissions ?? [],
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({ role: data }))
    const onSuccess = () => onOpenChange(false)

    if (isEdit && role) {
      form.put(`/roles/${role.id}`, { onSuccess, preserveScroll: true })
    } else {
      form.post('/roles', { onSuccess, preserveScroll: true })
    }
  }

  const handleTogglePermission = (permission: string, checked: boolean) => {
    const next = checked
      ? [...form.data.permissions, permission]
      : form.data.permissions.filter((p) => p !== permission)
    form.setData('permissions', next)
  }

  return (
    <Dialog className="w-[480px]"
      open={open}
      onOpenChange={(next) => {
        if (!next) form.clearErrors()
        onOpenChange(next)
      }}
    >
      <DialogHeader>
        <DialogTitle>{isEdit ? 'Edit role' : 'New role'}</DialogTitle>
        <DialogDescription>
          {isEdit ? 'Update details and permissions of this role.' : 'Create a new user role definition.'}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={submit} className="space-y-4">
        <InputField
          form={form}
          name="name"
          label="Name"
          placeholder="e.g. Content Moderator"
          required
        />

        <InputField
          form={form}
          name="key"
          label="Key"
          placeholder="e.g. content_moderator"
          hint="Lowercase letters and underscores only. Cannot be changed after creation."
          required
          disabled={isEdit}
        />

        <TextareaField
          form={form}
          name="description"
          label="Description"
          placeholder="Describe what users with this role can do…"
          rows={2}
        />

        <Field label="Permissions" error={form.errors.permissions}>
          <div className="border-border bg-bg-faint grid grid-cols-2 gap-2 rounded-md border p-3">
            {availablePermissions.map((perm) => (
              <label
                key={perm}
                className="hover:text-fg flex cursor-pointer items-center gap-2 py-1 text-sm transition-colors duration-100 ease-out select-none"
              >
                <Checkbox
                  checked={form.data.permissions.includes(perm)}
                  onCheckedChange={(checked) => handleTogglePermission(perm, checked)}
                />
                {perm}
              </label>
            ))}
          </div>
        </Field>

        <DialogFooter className="pt-2 border-t border-border/60">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" disabled={form.processing}>
            {form.processing
              ? isEdit
                ? 'Saving…'
                : 'Creating…'
              : isEdit
                ? 'Save changes'
                : 'Create role'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}
